import { getApiBaseUrl } from '../utils/apiOrigin';

export interface PublicAssistantUiConfig {
  title: string;
  subtitle: string;
  greeting: string;
  placeholder: string;
  suggestedQuestions: string[];
  disclaimer: string;
  showSources: boolean;
}

export interface PublicAssistantConfigResponse {
  enabled: boolean;
  ui: PublicAssistantUiConfig;
  maxMessageChars: number;
}

export interface PublicAssistantSource {
  title: string;
  source: string;
}

export interface PublicAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const DEFAULT_UI_CONFIG: PublicAssistantUiConfig = {
  title: 'Assistant',
  subtitle: '',
  greeting: 'Bonjour ! Comment puis-je vous aider ?',
  placeholder: 'Écrivez votre message…',
  suggestedQuestions: [],
  disclaimer: '',
  showSources: false,
};

export const fetchPublicAssistantConfig = async (): Promise<PublicAssistantConfigResponse> => {
  const response = await fetch(`${getApiBaseUrl()}/public-assistant/config`);
  if (!response.ok) throw new Error('Configuration indisponible');
  return response.json();
};

interface StreamHandlers {
  onSources?: (sources: PublicAssistantSource[]) => void;
  onDelta: (text: string) => void;
  onError: (message: string) => void;
  onDone: () => void;
}

const parseSseBlock = (block: string): { event: string; data: string } | null => {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }

  if (!dataLines.length) return null;
  return { event, data: dataLines.join('\n') };
};

/**
 * Envoie la conversation et consomme la réponse en flux (Server-Sent Events).
 * `EventSource` ne sait pas faire de POST, d'où la lecture manuelle du corps.
 */
export const streamPublicAssistantReply = async (
  messages: PublicAssistantMessage[],
  handlers: StreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}/public-assistant/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
      signal,
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') return;
    handlers.onError('Connexion impossible. Vérifiez votre connexion internet.');
    handlers.onDone();
    return;
  }

  if (!response.ok || !response.body) {
    const payload = await response.json().catch(() => null);
    handlers.onError(payload?.error || 'Une erreur est survenue. Réessayez dans un instant.');
    handlers.onDone();
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() || '';

      for (const block of blocks) {
        const parsed = parseSseBlock(block);
        if (!parsed) continue;

        let payload: any;
        try {
          payload = JSON.parse(parsed.data);
        } catch {
          continue;
        }

        if (parsed.event === 'delta') handlers.onDelta(payload.text || '');
        else if (parsed.event === 'sources') handlers.onSources?.(payload.sources || []);
        else if (parsed.event === 'error') handlers.onError(payload.message || 'Erreur inattendue.');
      }
    }
  } catch (error) {
    if ((error as Error)?.name !== 'AbortError') {
      handlers.onError('La connexion a été interrompue.');
    }
  } finally {
    handlers.onDone();
  }
};
